import React, { useCallback, useEffect, useRef, useState } from 'react'

import config from '../../config.json'
import Modal from '../../components/modal'
import GridGenerator from '../../components/grid/gridGenerator'

import {
  fetchNotifications,
  formatNotificationDate,
  markNotificationRead,
} from './notificationsApi'
import NotificationDetail from './NotificationDetail'

const normalizeListBody = (body) => {
  if (body && Array.isArray(body.rows)) return body
  if (Array.isArray(body)) return { rows: body, totalRecords: body.length }
  return { rows: [], totalRecords: 0 }
}

const NotificationsModal = ({ onClose, onRead, Alert }) => {
  const alertRef = useRef(Alert)
  alertRef.current = Alert

  const [gridPayload, setGridPayload] = useState(null)
  const [gridOptions, setGridOptions] = useState(null)
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(config.defaultItemsPerPage)
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState(null)
  const [empty, setEmpty] = useState(false)

  const buildGrid = useCallback((body, pg, ipp) => {
    const normalized = normalizeListBody(body)
    const rows = normalized.rows
    const dataPrint = {
      title: ['Fecha', 'Título', 'Mensaje', 'Estado'],
      data: rows.map((item) => ({
        id: item.notificationId,
        print: [
          formatNotificationDate(item.createdAt),
          item.title,
          item.preview || item.body,
          item.readAt ? 'Leída' : 'Sin leer',
        ],
        rowData: item,
        style: item.readAt ? 'notification-row-read' : 'notification-row-unread',
      })),
    }

    setEmpty(rows.length === 0)
    setGridPayload(dataPrint)
    setGridOptions({
      title: 'Notificaciones',
      page: pg,
      itemsPerPage: ipp,
      totalRecords: normalized.totalRecords ?? rows.length,
      download: true,
    })
  }, [])

  const loadNotifications = useCallback((pg, ipp) => {
    setLoading(true)
    setEmpty(false)
    setGridPayload(null)
    setGridOptions(null)

    return fetchNotifications(pg, ipp)
      .then((data) => {
        if (data.status === 'error') {
          alertRef.current?.({
            msg: data.msg || 'No se pudieron cargar las notificaciones',
            type: 'error',
          })
          setEmpty(true)
          return
        }
        buildGrid(data.body, pg, ipp)
      })
      .catch(() => {
        alertRef.current?.({ msg: config.fetchErrorMsg, type: 'error' })
        setEmpty(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [buildGrid])

  useEffect(() => {
    loadNotifications(1, config.defaultItemsPerPage)
  }, [loadNotifications])

  const handlePages = (_e, pg, ipp) => {
    setPage(pg)
    setItemsPerPage(ipp)
    loadNotifications(pg, ipp)
  }

  const openDetail = async (rowData) => {
    if (!rowData) return
    setDetail(rowData)

    if (!rowData.readAt) {
      const data = await markNotificationRead(rowData.notificationId)
      if (data.status === 'ok') {
        if (typeof onRead === 'function') onRead()
        loadNotifications(page, itemsPerPage)
      }
    }
  }

  const handleGridClick = (_column, rowData) => {
    openDetail(rowData)
  }

  return (
    <>
      <Modal
        title="Notificaciones"
        modalId="notifications-all-modal"
        modalClose={onClose}
        Alert={Alert}
        width="92%"
      >
        {loading && <p className="notifications-loading">Cargando notificaciones…</p>}
        {!loading && empty && (
          <p className="notifications-empty">No hay notificaciones para mostrar.</p>
        )}
        {!loading && !empty && gridPayload && gridOptions && (
          <GridGenerator
            data={gridPayload}
            options={gridOptions}
            rowClick={handleGridClick}
            handlePages={handlePages}
          />
        )}
      </Modal>

      {detail && (
        <Modal
          title={detail.title || 'Notificación'}
          modalId="notification-detail-modal"
          modalClose={() => setDetail(null)}
          Alert={Alert}
          width="640px"
        >
          <NotificationDetail notification={detail} />
        </Modal>
      )}
    </>
  )
}

export default NotificationsModal
